/**
 * The gardener's name, how to reach them, and where their garden is.
 *
 * The email is shown, not edited: it is the address they sign in with, set
 * at sign-up, and garden invitations are checked against it.
 *
 * A screen of its own, opened from the profile's first row, with the picture
 * at the top and one Save at the foot. Saving the picture, the links or the
 * public-page choices used to reset this form from the account they had just
 * updated, which quietly threw away whatever had been typed here and not yet
 * saved; the form now refills only when it opens.
 *
 * The address is not administrative trivia here: the postcode is what the
 * whole planting calendar is calculated from, so saving a new one throws away
 * the planting windows that were worked out for the old one. The garden's
 * place is the ZIP's centre, set by the server on Save (R18; W24 Job E): the
 * street, apartment and town are kept as text, and the browser asks no
 * geocoder where the street is.
 *
 * The ZIP is checked as it is typed, as on Today: once it is a ZIP's shape,
 * the backend says whether it is a real US place Grow can plan for, and the
 * place shows under the box ("✓ Sparta, TN") or why not. Save waits for a
 * yes, and it cannot be left empty. The City beside it is filled in (with
 * the State) from that same answer whenever the ZIP changes (the server sets
 * them the same way on Save, B100); it is editable under an unchanged ZIP, and
 * required.
 * (Accounts saved before this have it filled by private/sql/2026-10-09-user-city.sql
 * in the backend; one still empty is filled from its ZIP as the screen opens.) The country is the United States: Grow
 * plans from US ZIP codes only for now, and the backend sets it from the ZIP.
 *
 * In someone else's garden (B37; owner, 2026-10-20) the postcode here is the
 * member's own and does not move the garden being tended, whose place is its
 * owner's (the server reads the owner's): so it is not offered as the
 * garden's. Its label is plain "ZIP Code", not "sets your weather", and a line
 * under it says the garden's weather and frost dates follow its owner's ZIP
 * (useOwnerOnly). The member can still keep their own, for their own garden.
 *
 * A ZIP check or a save that fails on our side (a 5xx or no answer) says the
 * standard sentence, reported to Sentry, with the support address as a link
 * (D31); a ZIP check that failed used to leave Save saying "Checking" for
 * ever.
 *
 * Used by: UserProfile (?s=details).
 *
 * @module ProfileDetailsForm
 */

import { retryRule } from '@/config/queryRetry';
import { isUsablePostcode } from '@/shared/lib/postcode';
import { errorMessage } from '@/shared/lib/errorMessage';
import { isServerFailure } from '@/shared/lib/serverFailure';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { checkPostcode, placeOf, postcodeCheckKey, postcodeRefusal } from '@/shared/api/postcodes';
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Input } from '@/shared/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { t as translate } from '@/shared/i18n/i18nSetup';
import { useAuth } from '@/shared/hooks/useAuth';
import { updateUserProfile } from '@/shared/api/users';
import { debugLog } from '@/config/config';
import { ProfileScreen } from './ProfileScreen';
import { ProfilePhotoCard } from './ProfilePhotoCard';
import { SaveFoot } from './profileKit';
import { ALERT_PREFERENCES_KEY } from './profileQueries';
import { PLANTS_QUERY_KEY } from '@/config/tunables/ui';
import { QUERY_RETRY_COUNT } from '@/config/tunables/shared';
import { useOwnerOnly } from '@/shared/hooks/useOwnerOnly';

/** The ZIP is required; everything else is the gardener's business. */
const profileFormSchema = z.object({
  display_name: z.string().optional(),
  phone: z.string().optional(),
  address_line1: z.string().optional(),
  address_line2: z.string().optional(),
  /*
   * Required, like the ZIP: Today names the garden by it ("Sparta, TN"). It
   * is filled in from the ZIP (below), so it is only ever asked for in full
   * when the gardener has cleared it or the ZIP could not name a town.
   */
  city: z.string().refine(
    (value) => value.trim().length > 0,
    () => ({ message: translate('profile.info.address.cityRequired') }),
  ),
  state_province: z.string().optional(),
  /*
   * Checked, because everything downstream is keyed on it: the forecast, the
   * frost dates, every planting window and every plant's outlook. A typo
   * here does not fail loudly, it quietly plans the wrong garden.
   *
   * US ZIP codes only for now: the forecast is the National Weather
   * Service's. Never empty: a garden without one has no forecast and no
   * planting dates. Whether it is a real place Grow can plan for, the
   * backend says as it is typed (below), and again on save.
   */
  postal_code: z.string().refine(
    (value) => isUsablePostcode(value),
    // A function, so the words are looked up as the form is checked, in
    // whatever language the gardener reads by then.
    () => ({ message: translate('profile.info.address.zipFormat') }),
  ),
  country: z.string().refine(
    (value) => value.length >= 1,
    () => ({ message: translate('profile.info.address.countryRequired') }),
  ),
});

/** The form's values, as the schema checks them. */
type ProfileFormValues = z.infer<typeof profileFormSchema>;

/**
 * The states, and DC, for the address's state box, by code; each is named
 * from the dictionary (profile.info.address.states) in the reader's language.
 */
const US_STATES: string[] = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC',
];

/** The country every garden is in for now: Grow plans from US ZIP codes only. */
const COUNTRY = 'US';

/** The form's values for a user as the server has them. */
const valuesOf = (user: ReturnType<typeof useAuth>['mysqlUser']): ProfileFormValues => ({
  display_name: user?.display_name || '',
  phone: user?.phone || '',
  address_line1: user?.address_line1 || '',
  address_line2: user?.address_line2 || '',
  city: user?.city || '',
  state_province: user?.state_province || '',
  postal_code: user?.postal_code || '',
  country: COUNTRY,
});

/** The details screen: picture, name, contact, address and ZIP, with one Save. */
export const ProfileDetailsScreen: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { reactTranslate: t } = useTranslation();
  const { currentUser, mysqlUser, updateMysqlUser } = useAuth();
  /** True in someone else's garden: the postcode here is the member's own, not the garden's (B37). */
  const { tending } = useOwnerOnly();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: valuesOf(mysqlUser),
  });
  const dirty = form.formState.isDirty;

  // Filled from the account as the screen opens, and not again while it is
  // open: anything else saved meanwhile must not undo what is being typed.
  useEffect(() => {
    if (!open) return;
    form.reset(valuesOf(mysqlUser));
    setError(null);
    setJustSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** The postcode as typed, watched as the box changes. */
  const watchedZip = form.watch('postal_code');
  /** True once the box holds something other than the postcode on file. */
  const zipChanged = (watchedZip ?? '').trim() !== (mysqlUser?.postal_code ?? '').trim();

  // The place, once the box holds a ZIP's shape: one answer per ZIP, kept,
  // shared with Today's box and the check that re-asks a bad saved ZIP.
  const zip = (watchedZip ?? '').trim();
  const shaped = isUsablePostcode(zip);
  const { data: zipCheck, isFetching: zipChecking, error: zipCheckError } = useQuery({
    queryKey: postcodeCheckKey(zip),
    queryFn: () => checkPostcode(zip),
    enabled: open && shaped,
    staleTime: Infinity,
    retry: retryRule(QUERY_RETRY_COUNT),
  });
  // The town and state follow the ZIP: each time the backend places a ZIP,
  // its City and State fill in, as the server will set them on Save (B100;
  // the server decides, this only shows it early). A town typed after that
  // stays until the ZIP changes again. The ZIP on file keeps the town saved
  // with it (filling only an empty one), and going back to it brings that
  // town back. A ZIP with no town of its own still sets the State.
  useEffect(() => {
    if (!open || !zipCheck?.ok) return;
    const onFile = zipCheck.zip === (mysqlUser?.postal_code ?? '').trim();
    const city = (onFile ? mysqlUser?.city?.trim() : '') || zipCheck.city || '';
    const state = (onFile ? mysqlUser?.state_province?.trim() : '') || zipCheck.state || '';
    if (city && (form.getValues('city') ?? '') !== city) form.setValue('city', city, { shouldDirty: true, shouldValidate: form.formState.isSubmitted });
    if (state && (form.getValues('state_province') ?? '') !== state) form.setValue('state_province', state, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, zipCheck]);

  // A check that failed on our side says the standard sentence under the box,
  // rather than "Checking" for ever.
  const zipCheckFailed = shaped && !zipChecking && !zipCheck && isServerFailure(zipCheckError);

  /**
   * Saves the details: the form's values only. The server places the garden
   * from the ZIP (its centre), so no coordinates are sent.
   *
   * @param values - The form's values, already validated.
   * @returns True when saved; false with the reason shown when not.
   */
  const save = async (values: ProfileFormValues): Promise<boolean> => {
    if (!mysqlUser) return false;

    setIsSaving(true);
    setError(null);
    try {
      const updatedUser = await updateUserProfile(mysqlUser.user_id, values);
      const previousZip = mysqlUser.postal_code?.trim() || '';
      const nextZip = updatedUser.postal_code?.trim() || '';

      updateMysqlUser({ ...updatedUser, postal_code: updatedUser.postal_code ?? '' });
      form.reset(valuesOf({ ...mysqlUser, ...updatedUser }));
      setJustSaved(true);

      // Weather warnings read the phone and the postcode.
      void queryClient.invalidateQueries({ queryKey: ALERT_PREFERENCES_KEY });
      // A new postcode is a new climate: every planting window and every
      // plant's outlook was calculated for somewhere else.
      if (previousZip !== nextZip) {
        await queryClient.invalidateQueries({ queryKey: [PLANTS_QUERY_KEY] });
      }
      return true;
    } catch (err) {
      debugLog('UserProfile', 'Error updating user:', err);
      setError(postcodeRefusal(err, values.postal_code ?? '') ?? errorMessage(err, t('profile.saveFailed')));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Validates, then saves; false when either stops it. A ZIP the backend has
   * not said yes to is not saved: still being checked, or refused, it says so
   * under the box.
   *
   * @returns True when saved.
   */
  const submit = (): Promise<boolean> => new Promise((resolve) => {
    void form.handleSubmit(
      async (values) => {
        if (!zipCheck?.ok) {
          // The standard sentence is already under the box.
          if (zipCheckFailed) { resolve(false); return; }
          form.setError('postal_code', {
            message: zipCheck && !zipCheck.ok ? zipCheck.message : t('welcome.zip.checking'),
          });
          resolve(false);
          return;
        }
        resolve(await save(values));
      },
      () => resolve(false),
    )();
  });

  return (
    <ProfileScreen
      open={open}
      title={t('profile.index.details')}
      onClose={onClose}
      dirty={dirty}
      onSave={submit}
      onDiscard={() => form.reset(valuesOf(mysqlUser))}
      footer={<SaveFoot dirty={dirty} saving={isSaving} error={error} justSaved={justSaved} onSave={() => { void submit(); }} />}
    >
      <ProfilePhotoCard />

      <Form {...form}>
        <form onSubmit={(event) => { event.preventDefault(); void submit(); }} className="space-y-4">
          <FormField
            control={form.control}
            name="display_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('profile.info.displayName')}</FormLabel>
                <FormControl>
                  <Input autoComplete="name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Shown, not edited: it is the address they sign in with, and
              garden invitations are checked against it. */}
          <div className="space-y-1">
            <p className="text-sm font-medium">{t('profile.info.email')}</p>
            <p className="break-all text-[0.9375rem]">{currentUser?.email || mysqlUser?.email}</p>
            <p className="text-xs text-muted-foreground">{t('profile.info.emailHint')}</p>
          </div>

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('profile.info.phoneForTexts')}</FormLabel>
                <FormControl>
                  <Input type="tel" autoComplete="tel" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address_line1"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('profile.info.address.street')}</FormLabel>
                <FormControl>
                  <Input autoComplete="address-line1" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address_line2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('profile.info.address.aptOptional')}</FormLabel>
                <FormControl>
                  <Input autoComplete="address-line2" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('profile.info.address.city')}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="address-level2"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="state_province"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('profile.info.address.state')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('profile.info.address.state')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {US_STATES.map((code) => (
                        <SelectItem key={code} value={code}>{t(`profile.info.address.states.${code}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="postal_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t(tending ? 'profile.info.address.zipCode' : 'profile.info.address.zipForWeather')}</FormLabel>
                  <FormControl>
                    <Input inputMode="numeric" autoComplete="postal-code" placeholder={t('welcome.zip.placeholder')} {...field} />
                  </FormControl>
                  {/* A member's own ZIP does not move the garden they tend (B37). */}
                  {tending && <p className="text-xs text-muted-foreground">{t('garden.ownersPlace')}</p>}
                  {/* Where it is, or why not, as it is typed (as on Today). */}
                  {zip && !shaped && !form.formState.errors.postal_code && (
                    <p className="text-xs text-warning">{t('welcome.zip.invalid')}</p>
                  )}
                  {shaped && zipChecking && !zipCheck && (
                    <p className="text-xs text-muted-foreground">{t('welcome.zip.checking')}</p>
                  )}
                  {zipCheckFailed && (
                    <ServerFailureNote error={zipCheckError} where="ProfileDetailsForm" className="text-xs text-destructive" />
                  )}
                  {shaped && zipCheck?.ok && !form.formState.errors.postal_code && (
                    <p className="flex items-center gap-1 text-sm font-medium text-primary">
                      <Check className="h-4 w-4 shrink-0" aria-hidden="true" />{placeOf(zipCheck)}
                    </p>
                  )}
                  {shaped && zipCheck && !zipCheck.ok && !form.formState.errors.postal_code && (
                    <p className="text-xs text-warning">{zipCheck.message}</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('profile.info.address.country')}</FormLabel>
                  {/* The United States, said rather than chosen: Grow plans
                      from US ZIP codes only for now, and the backend sets the
                      country from the ZIP. It offered Canada, Mexico and the
                      UK, which no ZIP check would accept. */}
                  <FormControl>
                    <Input readOnly value={t(`profile.info.address.countries.${field.value || COUNTRY}`)} className="bg-muted/50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Said while they are changing it, not before. A standing warning
              under an unchanged field is furniture; one that appears the
              moment the number differs is a question. */}
          {zipChanged && (
            <p className="flex items-start gap-1.5 text-sm text-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t('profile.info.address.zipWarning')}</span>
            </p>
          )}
        </form>
      </Form>
    </ProfileScreen>
  );
};
